/* ==========================================================================
   STEEL & SADDLE, PRODUCT
   Variant selection, gallery, and the sticky mobile buy bar.
   Custom element so it initialises correctly inside the theme editor.
   ========================================================================== */

class SSProduct extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();
    if (!this.variants) return;

    this.form       = this.querySelector('[data-cart-form]');
    this.idInput    = this.querySelector('[data-variant-id]');
    this.priceZone  = this.querySelector('[data-price]');
    this.availZone  = this.querySelector('[data-availability]');
    this.submitBtn  = this.querySelector('[data-cart-submit]');
    this.stickyBar  = document.querySelector('[data-sticky-buy]');
    this.bisZone    = this.querySelector('[data-bis]');
    this.bisNote    = this.querySelector('[data-bis-note]');

    /* Merchant-editable labels, so changing "Add to cart" in the customiser
       is not silently reverted the first time a shopper changes size. */
    const cfg = (window.SSConfig && window.SSConfig.strings) || {};
    /* v59: the visible label now carries size and price, so the base wording
       comes from data-base-label rather than from the button's text. */
    const serverLabel = this.submitBtn && this.submitBtn.dataset.baseLabel;
    this.strings = {
      addToCart:   serverLabel || cfg.addToCart || 'Add to cart',
      soldOut:     cfg.soldOut || 'Sold out',
      unavailable: cfg.unavailable || 'Unavailable'
    };

    /* v80: no size is picked until the shopper picks one. The section marks
       the element data-needs-pick; until then the form refuses to submit, the
       button reads "Pick a size" and the one-tap pay buttons stay hidden. */
    this.needsPick = this.hasAttribute('data-needs-pick');
    this.selected = this.needsPick ? null : this.currentVariant();
    this.errorBox = this.querySelector('[data-cart-error]');
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        if (!this.needsPick) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.promptPick();
      }, true);
    }
    if (this.selected && !this.selected.available) this.toggleExpress(false);

    /* v59 stock status */
    this.stockMap = this.readJSON('[data-stock-json]') || {};
    this.stockShip = this.querySelector('[data-stock-ship]');
    this.lowAt = this.availZone ? parseInt(this.availZone.dataset.lowAt, 10) || 3 : 3;
    this.showCount = !this.availZone || this.availZone.dataset.showCount !== 'false';
    this.defaultOnly = this.variants.length === 1 && this.variants[0].title === 'Default Title';
    this.sizeOnly = this.variants.every((v) => v.options.length === 1)
      && !!this.querySelector('.pdp__option .t-label')
      && this.querySelector('.pdp__option .t-label').textContent.trim() === 'Size';

    this.bindSwatches();
    /* v80: strike through sold-out sizes on load, not only after the first
       tap. SimGym shoppers tapped size after size to find each one gone. */
    this.markUnavailable(this.selectedOptions());
    this.bindGallery();
    this.bindSticky();
  }

  /* Tapping Add to cart, or the sticky bar, before a size: say so, bring the
     picker into view and put focus on it. Nothing is added. */
  promptPick() {
    const group = this.querySelector('[data-option-group]');
    const raw = (this.submitBtn && this.submitBtn.dataset.pickLabel) || 'Pick a size';
    const base = raw.charAt(0).toUpperCase() + raw.slice(1);
    if (this.errorBox) {
      this.errorBox.textContent = base + ' first.';
      this.errorBox.hidden = false;
    }
    if (!group) return;
    group.classList.remove('is-prompt');
    void group.offsetWidth; /* restart the nudge if tapped twice */
    group.classList.add('is-prompt');
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    group.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    const first = group.querySelector('.swatch:not(.is-unavailable)') || group.querySelector('.swatch');
    if (first) first.focus({ preventScroll: true });
    clearTimeout(this.promptTimer);
    this.promptTimer = setTimeout(() => group.classList.remove('is-prompt'), 1600);
  }

  readJSON(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch { return null; }
  }

  sizePhrase(variant) {
    if (this.defaultOnly) return '';
    return (this.sizeOnly ? ' in size ' : ' in ') + variant.title;
  }

  /* "Add to cart · M · $54.00". Dots, never dashes. */
  fullLabel(variant) {
    const parts = [this.strings.addToCart];
    if (!this.defaultOnly) parts.push(variant.title);
    parts.push(SS.money(variant.price));
    return parts.join(' · ');
  }

  readVariants() {
    const node = this.querySelector('[data-variant-json]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch { return null; }
  }

  currentVariant() {
    const id = this.idInput && parseInt(this.idInput.value, 10);
    return this.variants.find((v) => v.id === id) || this.variants[0];
  }

  selectedOptions() {
    const opts = [];
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const idx = parseInt(group.getAttribute('data-option-group'), 10);
      const active = group.querySelector('.swatch.is-selected');
      opts[idx] = active ? active.getAttribute('data-value') : null;
    });
    return opts;
  }

  /* The picker is role="radiogroup" / role="radio", so it owes the reader the
     full native contract: aria-checked, a roving tabindex, and arrow keys.
     Without the roving tabindex a keyboard user has to Tab through every size
     to reach the one they want, which is exactly what the radio pattern
     exists to avoid. */
  bindSwatches() {
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const swatches = Array.from(group.querySelectorAll('.swatch'));

      const select = (btn, focus) => {
        swatches.forEach((s) => {
          const on = s === btn;
          s.classList.toggle('is-selected', on);
          s.setAttribute('aria-checked', on ? 'true' : 'false');
          s.setAttribute('tabindex', on ? '0' : '-1');
        });
        if (focus) btn.focus();
        this.onOptionChange();
      };

      swatches.forEach((btn) => {
        btn.addEventListener('click', () => select(btn, false));
      });

      group.addEventListener('keydown', (e) => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
        if (!keys.includes(e.key)) return;
        const current = swatches.indexOf(document.activeElement);
        if (current === -1) return;
        e.preventDefault();
        let next = current;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % swatches.length;
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   next = (current - 1 + swatches.length) % swatches.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End')  next = swatches.length - 1;
        select(swatches[next], true);
      });
    });
  }

  onOptionChange() {
    const chosen = this.selectedOptions();
    const match = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));

    this.markUnavailable(chosen);

    if (!match) { this.renderUnavailable(); return; }

    this.selected = match;
    if (this.idInput) this.idInput.value = match.id;
    if (this.needsPick) {
      this.needsPick = false;
      this.removeAttribute('data-needs-pick');
      if (this.errorBox) { this.errorBox.hidden = true; this.errorBox.textContent = ''; }
    }

    this.renderPrice(match);
    this.renderAvailability(match);
    this.renderButton(match);
    this.syncGalleryToVariant(match);
    this.syncSticky(match);
    this.syncUrl(match);
  }

  /* Grey out option values that produce no purchasable combination */
  markUnavailable(chosen) {
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const idx = parseInt(group.getAttribute('data-option-group'), 10);
      group.querySelectorAll('.swatch').forEach((btn) => {
        const trial = chosen.slice();
        trial[idx] = btn.getAttribute('data-value');
        const exists = this.variants.some(
          (v) => v.available && v.options.every((o, i) => trial[i] == null || o === trial[i])
        );
        /* State was conveyed by a 0.35 opacity fade alone: invisible to a
           screen reader and 1.45:1 to everyone else, on a control that still
           took a click. */
        btn.classList.toggle('is-unavailable', !exists);
        btn.setAttribute('aria-disabled', exists ? 'false' : 'true');
        const state = btn.querySelector('[data-swatch-state]');
        if (state) state.textContent = exists ? '' : ' (sold out)';
      });
    });
  }

  renderPrice(variant) {
    if (!this.priceZone) return;
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    this.priceZone.innerHTML = onSale
      ? `<span class="pdp__price pdp__price--sale">${SS.money(variant.price)}</span>
         <s class="pdp__price-was">${SS.money(variant.compare_at_price)}</s>
         <span class="badge badge--sale">Sale</span>`
      : `<span class="pdp__price">${SS.money(variant.price)}</span>`;
  }

  /* v59 stock status. Same three states the section renders server side:
     in (green, no count), low (amber, at or under the threshold, count),
     out (red). No dashes anywhere in this copy. */
  renderAvailability(variant) {
    if (!this.availZone) return;
    const s = this.stockMap[String(variant.id)] || { q: 0, t: false };
    const where = this.sizePhrase(variant);
    let state, status, count, on;
    if (!variant.available) {
      state = 'out'; status = 'Sold out'; on = 0;
      count = (where ? 'Nothing left' + where + '. ' : '') + 'Get on the list below.';
    } else if (s.t && s.q <= this.lowAt) {
      state = 'low'; status = 'Almost gone'; on = 1;
      count = this.showCount ? 'Only ' + s.q + ' left' + where : '';
    } else {
      state = 'in'; status = 'In stock';
      on = s.t ? Math.min(5, Math.max(2, Math.ceil(s.q / 4))) : 5;
      count = ''; /* v65: no number while stock is healthy */
    }
    this.availZone.className = 'stock stock--' + state;
    const bars = Array.from({ length: 5 }, (_, i) => '<i' + (i < on ? ' class="is-on"' : '') + '></i>').join('');
    this.availZone.innerHTML =
      '<span class="stock__dot" aria-hidden="true"></span>' +
      '<span class="stock__text"><strong class="stock__status">' + status + '</strong>' +
      (count ? '<span class="stock__count">' + count + '</span>' : '') + '</span>' +
      '<span class="stock__meter" aria-hidden="true">' + bars + '</span>';
    if (this.stockShip) this.stockShip.hidden = !variant.available;
  }

  renderButton(variant) {
    if (!this.submitBtn) return;
    const label = this.submitBtn.querySelector('[data-submit-label]') || this.submitBtn;
    this.submitBtn.disabled = !variant.available;
    label.textContent = variant.available ? this.fullLabel(variant) : this.strings.soldOut;
    this.toggleExpress(variant.available);
    this.toggleBackInStock(variant);
  }

  /* Follows the size picker rather than only reflecting the size the page
     happened to load on. */
  toggleBackInStock(variant) {
    if (!this.bisZone) return;
    this.bisZone.hidden = !!(variant && variant.available);
    if (this.bisNote && variant) {
      this.bisNote.value = 'Wants ' + document.title.split('|')[0].trim() + ' in ' + (variant.title || 'any size');
    }
  }

  /* v80: no one-tap pay on a size that cannot ship. Inline so the
     needs-pick rule in ss-fixes.css still wins before a size is picked. */
  toggleExpress(on) {
    const box = this.querySelector('[data-express]');
    if (box) box.style.display = on ? '' : 'none';
  }

  renderUnavailable() {
    this.toggleExpress(false);
    if (this.submitBtn) {
      const label = this.submitBtn.querySelector('[data-submit-label]') || this.submitBtn;
      this.submitBtn.disabled = true;
      label.textContent = this.strings.unavailable;
    }
    if (this.availZone) {
      this.availZone.className = 'stock stock--out';
      this.availZone.innerHTML = '<span class="stock__dot" aria-hidden="true"></span><span class="stock__text"><strong class="stock__status">Unavailable</strong><span class="stock__count">That combination is not made.</span></span><span class="stock__meter" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></span>';
      if (this.stockShip) this.stockShip.hidden = true;
    }
    if (this.bisZone) this.bisZone.hidden = false;
  }

  syncUrl(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  /* ---------------------------------------------------------------------
     Gallery
     --------------------------------------------------------------------- */
  bindGallery() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.dots   = Array.from(this.querySelectorAll('[data-dot]'));
    this.index  = 0;
    if (!this.slides.length) return;

    /* Position readout and end-stops. Shoppers in testing kept cycling the
       gallery and revisiting images because nothing reported where they were
       or that they had seen everything. Deliberately does NOT wrap around:
       hitting a hard stop is the signal that the gallery is exhausted. */
    const counter = this.querySelector('[data-gallery-index]');
    const prevBtn = this.querySelector('[data-gallery-prev]');
    const nextBtn = this.querySelector('[data-gallery-next]');

    const paintNav = () => {
      if (counter) counter.textContent = this.index + 1;
      if (prevBtn) prevBtn.disabled = this.index === 0;
      if (nextBtn) nextBtn.disabled = this.index === this.slides.length - 1;
    };

    const go = (i) => {
      if (i < 0 || i >= this.slides.length || i === this.index) return;
      [this.slides, this.thumbs, this.dots].forEach((set) => {
        if (set[this.index]) set[this.index].classList.remove('is-active');
        if (set[i]) set[i].classList.add('is-active');
      });
      if (this.thumbs[i]) this.thumbs[i].setAttribute('aria-current', 'true');
      if (this.thumbs[this.index]) this.thumbs[this.index].removeAttribute('aria-current');
      this.index = i;
      paintNav();
    };
    this.goToSlide = go;
    paintNav();

    this.thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
    this.dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    if (prevBtn) prevBtn.addEventListener('click', () => go(this.index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => go(this.index + 1));

    // Swipe on touch
    const stage = this.querySelector('[data-gallery-stage]');
    if (stage) {
      /* It is a focusable div with arrow-key behaviour, so it needs a role
         and a name or a keyboard user lands on an unnamed generic box. */
      stage.setAttribute('role', 'group');
      stage.setAttribute('aria-roledescription', 'carousel');
      stage.setAttribute('aria-label', 'Product images, use the left and right arrow keys');
      let startX = 0;
      stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        const dx = startX - e.changedTouches[0].clientX;
        if (Math.abs(dx) > 45) go(this.index + (dx > 0 ? 1 : -1));
      }, { passive: true });
    }

    // Arrow keys when the gallery has focus
    if (stage) {
      stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(this.index + 1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); go(this.index - 1); }
      });
    }
  }

  syncGalleryToVariant(variant) {
    if (!this.goToSlide || !variant.featured_media) return;
    const target = this.slides.findIndex(
      (s) => parseInt(s.getAttribute('data-media-id'), 10) === variant.featured_media.id
    );
    if (target > -1) this.goToSlide(target);
  }

  /* ---------------------------------------------------------------------
     Sticky mobile buy bar
     --------------------------------------------------------------------- */
  bindSticky() {
    if (!this.stickyBar || !this.submitBtn) return;

    const stickyBtn = this.stickyBar.querySelector('[data-sticky-submit]');
    if (stickyBtn) stickyBtn.addEventListener('click', () => this.submitBtn.click());

    /* v67: size dropdown in the bar. Tapping the matching swatch keeps one
       source of truth: everything downstream runs through onOptionChange. */
    this.stickyOption = this.stickyBar.querySelector('[data-sticky-option]');
    if (this.stickyOption) {
      this.stickyOption.addEventListener('change', () => {
        const value = this.stickyOption.value;
        const swatch = Array.from(this.querySelectorAll('[data-option-group="0"] .swatch'))
          .find((s) => s.getAttribute('data-value') === value);
        if (swatch) swatch.click();
      });
    }

    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => this.stickyBar.classList.toggle('is-visible', !entry.isIntersecting),
      { threshold: 0.05 }
    );
    io.observe(this.submitBtn);
  }

  syncSticky(variant) {
    if (!this.stickyBar) return;
    const price = this.stickyBar.querySelector('[data-sticky-price]');
    const btn   = this.stickyBar.querySelector('[data-sticky-submit]');
    if (price) price.textContent = SS.money(variant.price);
    if (btn) {
      btn.disabled = !variant.available;
      const detail = this.stickyOption ? SS.money(variant.price) : (this.defaultOnly ? '' : variant.title);
      btn.textContent = variant.available
        ? this.strings.addToCart + (detail ? ' · ' + detail : '')
        : this.strings.soldOut;
    }
    if (this.stickyOption && variant.options && variant.options[0] != null) {
      this.stickyOption.value = variant.options[0];
    }
  }
}

if (!customElements.get('ss-product')) customElements.define('ss-product', SSProduct);

/* ==========================================================================
   IMAGE ZOOM

   Click, or pinch, to inspect the print. On an apparel page where the whole
   proposition is "original artwork, printed properly", not being able to look
   closely at the artwork is a real gap.

   Deliberately not a hover-magnifier: those need a pointer, break on touch,
   and are the single most dated pattern in ecommerce. This is a full-screen
   view with native pinch-zoom on touch and click-to-zoom on a pointer.
   ========================================================================== */
SS.zoom = () => {
  const stage = document.querySelector('[data-gallery-stage]');
  if (!stage || stage.dataset.zoomBound === 'true') return;
  stage.dataset.zoomBound = 'true';

  let sheet, img, release;

  const close = () => {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    if (release) { release(); release = null; }
    setTimeout(() => { if (sheet && !sheet.classList.contains('is-open')) sheet.hidden = true; }, 300);
  };

  const build = () => {
    sheet = document.createElement('div');
    sheet.className = 'zoomer';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Product image, zoomed');
    sheet.hidden = true;
    sheet.innerHTML =
      '<button type="button" class="zoomer__close icon-btn" aria-label="Close zoom">' +
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5">' +
      '<path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<div class="zoomer__scroll"><img alt=""></div>' +
      '<p class="zoomer__hint">Pinch or scroll to look closer</p>';
    document.body.appendChild(sheet);
    img = sheet.querySelector('img');
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet || e.target.closest('.zoomer__close')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheet.classList.contains('is-open')) close();
    });
  };

  const open = (src, alt) => {
    if (!sheet) build();
    img.src = src;
    img.alt = alt || '';
    sheet.hidden = false;
    void sheet.offsetWidth;
    sheet.classList.add('is-open');
    document.body.classList.add('is-locked');
    if (SS.trapFocus) release = SS.trapFocus(sheet);
    const btn = sheet.querySelector('.zoomer__close');
    if (btn) requestAnimationFrame(() => btn.focus());
  };

  const sourceFor = (el) => {
    /* currentSrc is whatever the browser actually chose off the srcset, so
       widening it to 2400 gives a real zoom rather than an upscale. */
    const chosen = el.currentSrc || el.src || '';
    return chosen.replace(/(\?|&)width=\d+/, '$1width=2400');
  };

  stage.addEventListener('click', (e) => {
    const el = e.target.closest('img');
    if (!el) return;
    open(sourceFor(el), el.getAttribute('alt'));
  });
  stage.style.cursor = 'zoom-in';

  /* The stage is already focusable and arrow-key driven, so give it the
     keyboard route into zoom too. */
  stage.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const active = stage.querySelector('.pdp__slide.is-active img');
    if (!active) return;
    e.preventDefault();
    open(sourceFor(active), active.getAttribute('alt'));
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => SS.zoom(), { once: true });
} else {
  SS.zoom();
}
document.addEventListener('shopify:section:load', () => SS.zoom());
